import React from 'react';
import LabeledVerticalExpander from 'Components/Universal/Questionnaire/UI/LabeledVerticalExpander';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import SquareButton from 'Components/UI/SquareButton';
import RoundButton from 'Components/UI/RoundButton';
import withViewContext from 'Components/Universal/Questionnaire/Fields/withViewContext';
import { householdPlansUri } from 'Consts/advisorUris';
import useResource from 'Hooks/useResource';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { goalToLabel, goalToLabelNameAndEndDate } from 'Helpers/Goals';
import relationships from 'Consts/Relationships';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { advisorView } from 'Context/ViewContext';
import featureEnabled, { pickImplementation, pickComponentAndProps } from 'Helpers/Affordances';
import GoalQuestions from 'Components/UI/GoalFlow/GoalQuestions';
import useUserContext from 'Context/UserContext';
import GrowingProgressBar from 'Components/UI/GrowingProgressBar';
import BestNextStepCheckbox from 'Components/UI/BestNextStepCheckbox';
import { dispatchGoalsRefresh } from 'Hooks/useRefreshEvent';
import MissingDataBubble from 'Components/UI/MissingDataBubble';
import fr from 'facet-rest';

const useStyles = makeStyles(theme => ({
  createPlanButton: {
    marginTop: theme.spacing(2),
  },
  timeHorizon: {
    display: 'flex',
  },
  timeHorizonItem: {
    display: 'flex',
  },
  save: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  questions: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
}));

const ButtonCreatePlan = withViewContext(({ householdId, planPrompt }) => {
  const classes = useStyles();

  return (
    <RoundButton
      className={classes.createPlanButton}
      color="accentBlue"
      variant="contained"
      disableElevation
      component={Link}
      to={householdPlansUri(householdId)}
    >
      {planPrompt}
    </RoundButton>
  );
});

ButtonCreatePlan.propTypes = {
  householdId: PropTypes.number.isRequired,
  planPrompt: PropTypes.string.isRequired,
};

const GoalSubsection = ({
  householdId,
  record,
  removeButton,
  onSave,
  planPrompt,
  defaultExpanded,
}) => {
  const classes = useStyles();
  const { goalResource } = record;
  const { agentResource } = useUserContext();
  const { resource, setAttribute } = useResource(goalResource);
  const { householdResource } = useSimpleHousehold();


  function DefaultTimeHorizon() {
    return (
      <div className={classes.timeHorizon}>
        <MetaControl
          className={classes.timeHorizonItem}
          key="timeHorizonValue"
          attrName="timeHorizonValue"
          onChange={(value) => {
            setAttribute('timeHorizonValue', value);
          }}
          resource={resource}
          fullWidth
        />
        <MetaControl
          className={classes.timeHorizonItem}
          key="timeHorizonType"
          attrName="timeHorizonType"
          onChange={(value) => {
            setAttribute('timeHorizonType', value);
          }}
          resource={resource}
          fullWidth
        />
      </div>
    );
  }

  const { component: TimeHorizonComponent, props: timeHorizonProps } = pickComponentAndProps(householdResource, {
    [relationships.goalsPageAndBanner]: [MetaControl, {
      key: 'endDate',
      attrName: 'endDate',
      onChange: (value) => {
        setAttribute('endDate', value);
      },
      resource,
      fullWidth: true,
    }],
    [relationships.default]: [DefaultTimeHorizon, {}],
  });

  const { component: NameComponent, props: nameProps } = pickComponentAndProps(householdResource, {
    [relationships.goalsPageAndBanner]: [MetaControl, {
      key: 'name',
      attrName: 'name',
      onChange: (value) => {
        setAttribute('name', value);
      },
      resource,
      fullWidth: true,
    }],
    [relationships.default]: [null, {}],
  });

  const { component: QuestionsComponent, props: questionsProps } = pickComponentAndProps(goalResource, {
    [relationships.goalFlowToggle]: [GoalQuestions, {
      goalResource,
      className: classes.questions,
    }],
    [relationships.default]: [null, {}],
  });

  const MissingDataComponent = pickImplementation(agentResource, {
    [fr.relationships.default.name]: goalResource.attr('isMissingData') === 'true' && (
      <MissingDataBubble tooltipText="This goal is missing data." />
    ),
  });

  const subTitle = pickImplementation(agentResource, {
    [fr.relationships.default.name]: resource?.id()
      ? (
        <div style={{ marginTop: '-0.3em' }}>
          <GrowingProgressBar progress={resource.attr('percentComplete') || 0} />
        </div>
      ) : null,
  });

  return (
    <LabeledVerticalExpander
      transitionSpeed={0.15}
      title={featureEnabled(householdResource, relationships.goalsPageAndBanner) ? goalToLabelNameAndEndDate(resource) : goalToLabel(resource)}
      removeButton={removeButton}
      expanded={defaultExpanded}
      additionalActions={[<BestNextStepCheckbox updateGoalCollection={dispatchGoalsRefresh} goalResource={resource} />]}
      subTitle={subTitle}
      TitleComponent={MissingDataComponent}
    >
      { NameComponent && <NameComponent {...nameProps} /> }
      <MetaControl
        key="type"
        attrName="type"
        onChange={(value) => {
          setAttribute('type', value);
        }}
        resource={resource}
        fullWidth
      />
      <MetaControl
        key="description"
        attrName="description"
        onChange={(value) => {
          setAttribute('description', value);
        }}
        resource={resource}
        fullWidth
      />
      <MetaControl
        key="amount"
        attrName="amount"
        onChange={(value) => {
          setAttribute('amount', value);
        }}
        resource={resource}
        fullWidth
      />
      <MetaControl
        key="amount-saved"
        attrName="amountSaved"
        onChange={(value) => {
          setAttribute('amountSaved', value);
        }}
        resource={resource}
        fullWidth
      />
      {TimeHorizonComponent && <TimeHorizonComponent {...timeHorizonProps} /> }
      <MetaControl
        key="priority"
        attrName="priority"
        onChange={(value) => {
          setAttribute('priority', value);
        }}
        resource={resource}
        fullWidth
      />
      {QuestionsComponent && <QuestionsComponent {...questionsProps} /> }
      <ButtonCreatePlan
        requiredView={advisorView}
        householdId={householdId}
        planPrompt={planPrompt}
      />
      <div className={classes.save}>
        <SquareButton
          color="green"
          onClick={() => onSave(resource)}
          text="Save"
        />
      </div>
    </LabeledVerticalExpander>
  );
};

GoalSubsection.propTypes = {
  householdId: PropTypes.string.isRequired,
  record: PropTypes.object.isRequired,
  removeButton: PropTypes.node.isRequired,
  onSave: PropTypes.func.isRequired,
  planPrompt: PropTypes.string.isRequired,
  defaultExpanded: PropTypes.bool,
};

GoalSubsection.defaultProps = {
  defaultExpanded: false,
};

export default GoalSubsection;
